import { SavedTrackRef } from '../contracts/playback-controller';
// The selection's structural shape, from the context file — NOT the provider's
// `VoyageActiveVessel`, which this imported until the D7 amendment moved the
// track controller provider-side and made the leak visible. `SelectionPort`
// already declared `ActiveVesselRef` for exactly this, so the fix was to use
// the interface that was always there.
import { ActiveVesselRef, AisMapContext, SelectionPort } from '../contracts/ais-map.context';
import { ActiveVesselHalo } from './active-vessel-halo';

/**
 * What this module must be TOLD rather than know (rule R5 / phase-4
 * boundary). Each callback is a seam to code still living on the facade:
 */
export interface SelectionOptions {
  /**
   * The trail/halo colour a hull gets when its feed colour is unusable — the
   * provider's constant, injected because it lives with the provider's
   * track-history model.
   */
  defaultTrackColor: string;
  /**
   * Already-purchased tracks for an MMSI — replayable for free. Entitlement
   * state off the sensor document; moves with the track feature (phase 6).
   */
  savedTracksFor: (mmsi: string) => SavedTrackRef[];
  /**
   * Push price + balance into every priced control. Becomes the feature
   * `syncCredits` fan-out when features register (phase 5).
   */
  syncCreditLines: () => void;
  /** Tear down the drawn track (trail, ghost, marks) — track feature, phase 6. */
  clearTrackLayers: () => void;
}

/**
 * THE SELECTION — the vessel the user has picked, its pulsing halo, and the
 * reconciliation that keeps both honest across layer swaps. Extracted verbatim
 * from `VoyageMapController` (phase 4, REFACTOR-PLAN.md §7); peers are reached
 * through `ctx` ports only (rule R2).
 *
 * The selection deliberately outlives the layer it was made from — a refresh,
 * a history pick and a playback frame all swap the markers underneath it — so
 * most of this module is the machinery that re-points it at whatever is
 * actually drawn now, or takes the ring off the map when nothing is.
 */
export class SelectionController implements SelectionPort {
  /** The vessel the user has selected on the map (null = none). */
  active: ActiveVesselRef | null = null;
  /** Pulsing selection ring; rides the ghost during playback — the shared primitive every AIS provider's selection now draws through. */
  private readonly halo = new ActiveVesselHalo();

  constructor(
    private readonly ctx: AisMapContext,
    private readonly opts: SelectionOptions,
  ) {}

  /** The selected vessel's MMSI, or null — the port's live read. */
  activeMmsi(): string | null {
    return this.active?.mmsi ?? null;
  }

  /**
   * Install a SYNTHESISED selection — see `SelectionPort.setActive`. Writes
   * the field only: the caller owns the surrounding sequence (it deselects
   * first, then pushes the saved-track list and the toolbar's active vessel),
   * and that order is load-bearing — `setActiveVessel` tears down a loaded
   * track, so calling it out of turn would undo the replay being installed.
   */
  setActive(vessel: ActiveVesselRef | null): void {
    this.active = vessel;
  }

  /**
   * The halo's marker, or null — read by the track replay, which rides the
   * ring along the ghost frame by frame and puts it back on the hull's fix
   * when the track closes.
   */
  get haloMarker(): any {
    return this.halo.marker;
  }

  /**
   * Reconciles the selection with a live layer that has just REPLACED the one it
   * was made from — a refresh-history pick, a step through the archive, or a
   * fresh pull.
   *
   * Without this the selection survives the swap describing the layer that has
   * gone: the halo stays parked where the hull was in the previous snapshot, and
   * clicking that same hull again is a no-op, because `activateVessel` sees an
   * MMSI that is already selected. The vessel the user just clicked ends up the
   * one vessel on the map that is not highlighted.
   */
  reanchorActiveVessel(): void {
    if (!this.active) return;
    const marker = this.liveMarkerFor(this.active.mmsi);
    if (marker) {
      // No opacity to re-apply here: the traced MMSI survives the layer swap,
      // so `adoptLiveLayer`'s filter pass has already faded this hull if a
      // track is up. See `applyTypeFilterToLive`.
      this.anchorActive(marker);
      return;
    }
    // A track keeps its vessel selected even when that hull is not in the
    // snapshot: the path is still drawn and the toolbar still belongs to it.
    // There is simply no live marker to fade — see `_adoptTrackVessel`.
    if (this.ctx.host.track.hasTrack) return;
    // Nothing left to select: leaving it would park a halo over empty water and
    // offer a Trace CTA for a vessel that is not on the map.
    this.deactivateVessel();
  }

  /**
   * Puts the selection ring where the selected hull actually IS right now — or
   * takes it off the map when that hull is not drawn at all.
   *
   * The selection deliberately outlives the layer it was made from (a refresh, a
   * history pick and a playback frame all swap the markers underneath it), so
   * `this.active` alone never says whether there is a vessel on screen to ring.
   * Reading the drawn layer here is what stops the halo being left pulsing over
   * empty water — after the map is cleared, after the ship-type filter drops the
   * selected vessel's category, on any playback frame the vessel is absent from,
   * and for anything else the display pass decides not to paint.
   *
   * Runs at the END of the display pipeline (see `applyVesselDisplay`), because
   * it reads what that pipeline drew: a ring decided before the clustering pass
   * would be answering a question that pass had not settled yet.
   *
   * Hides rather than deselects: everything here is reversible from the toolbar
   * — unfilter the type, switch grouping off, step back to a snapshot the vessel
   * is in — and the ring comes straight back with the hull. Dropping the
   * selection is for when the vessel is gone for good (`reanchorActiveVessel`,
   * `dropSelectionWithMap`).
   */
  syncActiveHalo(layer: any): void {
    const active = this.active;
    if (!active) return;
    // A loaded track owns the halo: `renderTrackFrame` rides it along the ghost,
    // and the traced hull is held faded by the pass above — so reading the layer
    // here would take the ring off a track that is still playing.
    if (this.ctx.host.track.hasTrack) return;
    const marker = this.drawnMarkerFor(layer, active.mmsi);
    // Same reasoning as `anchorActive`: a position belongs to the SNAPSHOT, not
    // to the vessel, so the selection follows the marker it is drawn on rather
    // than keeping the coordinates it was first clicked at.
    const latlng =
      marker && typeof marker.getLatLng === 'function' ? marker.getLatLng() : null;
    if (latlng) {
      active.lat = latlng.lat;
      active.lng = latlng.lng;
    }
    this.placeHaloOn(marker, active);
  }

  /**
   * Rings `marker` — or takes the ring off the map when that marker is not one
   * the display pipeline actually painted.
   *
   * The ONE place the ring is put on a live/playback marker, so a filtered-out
   * or clustered hull cannot end up ringed by whichever call site ran last:
   * `syncActiveHalo` reaches here from the display pass, and `anchorActive` from
   * the selection being re-pointed at a layer that has just replaced its own.
   */
  private placeHaloOn(marker: any, active: ActiveVesselRef): void {
    if (!this.isMarkerPainted(marker)) {
      this.removeHalo();
      return;
    }
    this.applyActiveVisual(active);
  }

  /** A layer's marker for an MMSI, but only while it is actually painted. */
  drawnMarkerFor(layer: any, mmsi: string): any | null {
    if (!layer || typeof layer.eachLayer !== 'function') return null;
    let found: any = null;
    layer.eachLayer((l: any) => {
      if (found) return;
      const p = l?.feature?.properties ?? {};
      if (String(p['MMSI'] ?? p.mmsi ?? '') !== mmsi) return;
      if (!this.isMarkerPainted(l)) return;
      found = l;
    });
    return found;
  }

  /**
   * Whether a vessel marker is on screen right now.
   *
   * The icon element's own `display` is the ground truth, not `_vrVisible` /
   * `_vrClustered`: those record what the pipeline last INTENDED for a marker,
   * and the two drift whenever a layer is out of the pipeline's reach — the live
   * layer keeps the flags it was wearing when playback took it off the map, and
   * grouping switched off in the meantime rewrites neither.
   *
   * The flags are the fallback for a marker Leaflet has given no element: that
   * is a layer which is not on the map at all, where they are the only record of
   * what it looked like when it was.
   */
  private isMarkerPainted(marker: any): boolean {
    if (!marker) return false;
    const el = typeof marker.getElement === 'function' ? marker.getElement() : null;
    if (el) return (el as HTMLElement).style.display !== 'none';
    return marker._vrVisible !== false && marker._vrClustered !== true;
  }

  /** The live layer's marker for an MMSI, or null when that hull is not drawn. */
  liveMarkerFor(mmsi: string): any | null {
    const layer = this.ctx.snapshot.liveLayer;
    if (!layer || typeof layer.eachLayer !== 'function') return null;
    let found: any = null;
    layer.eachLayer((l: any) => {
      if (found) return;
      const p = l?.feature?.properties ?? {};
      if (String(p['MMSI'] ?? p.mmsi ?? '') === mmsi) found = l;
    });
    return found;
  }

  /**
   * Points the selection at `marker` — the same hull, in whatever layer is drawn
   * now.
   *
   * A vessel's position belongs to the SNAPSHOT, not to the vessel, so it is
   * re-read here rather than at each call site: `active.lat/lng` is what the halo
   * is drawn at, and what `clearTrackLayers` puts it back on when a track closes.
   */
  private anchorActive(marker: any, properties?: any): void {
    const active = this.active;
    if (!active) return;
    const latlng = typeof marker?.getLatLng === 'function' ? marker.getLatLng() : null;
    if (latlng) {
      active.lat = latlng.lat;
      active.lng = latlng.lng;
    }
    const p = properties ?? marker?.feature?.properties ?? {};
    active.timestampIso = p['Timestamp UTC'] ?? p['time_utc'] ?? active.timestampIso;
    // A loaded track owns the halo — `renderTrackFrame` rides it along the ghost,
    // so moving it onto the live marker here would fight the next frame.
    //
    // Through `placeHaloOn`, not straight to `applyActiveVisual`: the marker this
    // is re-pointed at is looked up by MMSI alone (`liveMarkerFor`), so it may be
    // one the display pass has filtered out or absorbed into a bubble — and
    // ringing it here would put back the ring that pass had just taken off.
    if (!this.ctx.host.track.hasTrack) this.placeHaloOn(marker, active);
  }

  // ── Track mode: selection ───────────────────────────────────────────────────
  /**
   * Selects the clicked vessel. Selection is purely local — no request, no
   * credit — and is what gives the trace, port-call and nearby drawers a
   * subject. All three buttons stand on the bar either way; what changes here is
   * what they are ABOUT.
   */
  activateVessel(properties: any, marker: any): void {
    const p = properties || {};
    const mmsi = String(p['MMSI'] ?? p.mmsi ?? '');
    if (!mmsi) return;
    // Every hull click passes through here, and every hull popup now carries
    // the "Get latest" button — make sure its click-delegate is armed.
    this.ctx.popup.ensurePopupActionsWired();
    // A loaded track is not given up by a click on another hull. Reading the
    // traffic around a path is most of what a path is for, and the vessels being
    // read are the dimmed ones — a stray click among them used to discard a PAID
    // track and put the map back where it was, with nothing to undo it.
    //
    // Leaflet still opens that vessel's popup on the same click (bound at the
    // layer, not here), so the information is on screen either way. Selecting a
    // different vessel now means closing the track first — the X on the track
    // bar, which is the deliberate act discarding it always should have been.
    if (this.ctx.host.track.hasTrack && this.active && this.active.mmsi !== mmsi) {
      return;
    }
    // One animation at a time: selecting a vessel pauses snapshot playback.
    if (this.ctx.host.playback.mode === 'playback') {
      this.ctx.host.playback.pause();
    }
    if (this.active?.mmsi === mmsi) {
      // Same vessel, but NOT necessarily the same marker: a history pick, a
      // refresh or a playback frame swaps the layer under the selection, and the
      // hull has usually moved between snapshots. Re-anchor instead of returning
      // — a bare return leaves the halo on the position it was first clicked at,
      // which is the one place the vessel no longer is.
      this.anchorActive(marker, p);
      return;
    }
    this.deactivateVessel();
    const latlng = typeof marker?.getLatLng === 'function' ? marker.getLatLng() : null;
    const rawColor = String(p['Color'] ?? '');
    this.active = {
      mmsi,
      name: String(p['Name'] ?? mmsi),
      color: /^#[0-9a-fA-F]{3,8}$/.test(rawColor) ? rawColor : this.opts.defaultTrackColor,
      lat: latlng ? latlng.lat : Number(p['Lat']),
      lng: latlng ? latlng.lng : Number(p['Lng']),
      timestampIso: p['Timestamp UTC'] ?? p['time_utc'],
      shipType: p['Ship Type'] ?? 'OTHER',
    };
    this.opts.syncCreditLines();
    this.ctx.host.track.setSavedTracks(this.opts.savedTracksFor(mmsi));
    this.ctx.host.track.setActiveVessel(this.active);
  }

  /**
   * The map has been EMPTIED — the ON MAP bar's clear, a snapshot switched back
   * off — so the selection goes with it: the halo, the trace drawer and the
   * track bar's "Trace · <vessel>" pill all describe a hull that is no longer on
   * screen and cannot be brought back without drawing a snapshot again.
   *
   * Distinct from `releaseLiveLayer`, which also runs while one snapshot is
   * being swapped for the next and must NOT throw the selection away — that is
   * what `reanchorActiveVessel` re-establishes on the layer that lands.
   *
   * A loaded track keeps its vessel selected: the path is still drawn over the
   * (now empty) map and the track bar still belongs to it, exactly as in
   * `reanchorActiveVessel`. Closing the track is the X on that bar.
   */
  dropSelectionWithMap(): void {
    if (!this.active || this.ctx.host.track.hasTrack) return;
    this.deactivateVessel();
  }

  /** Clears the selection, its halo and any track drawn for it. */
  deactivateVessel(): void {
    // The trace drawer is about the vessel being deselected — dismiss it too.
    this.ctx.host.toolbarDrawer.close();
    this.opts.clearTrackLayers();
    this.removeHalo();
    this.active = null;
    if (this.ctx.host.track.activeMmsi) this.ctx.host.track.setActiveVessel(null);
  }

  /**
   * Takes the selection ring off the map WITHOUT touching the selection itself.
   *
   * The two are separate because the hull can leave the map while the vessel is
   * still the one the toolbar belongs to — see `syncActiveHalo`.
   */
  private removeHalo(): void {
    this.halo.remove(this.ctx.host.map);
  }

  /** Creates/moves the pulsing selection ring behind the vessel glyph — the shared `ActiveVesselHalo` primitive every AIS provider's selection draws through. */
  applyActiveVisual(vessel: ActiveVesselRef | null): void {
    if (!vessel) {
      this.removeHalo();
      return;
    }
    this.halo.moveTo(this.ctx.host.map, [vessel.lat, vessel.lng], vessel.color);
  }

  /**
   * Nulls the halo reference WITHOUT touching the map — for the task-switch
   * reset, which runs inside a shared sweep that has already taken every layer
   * off (invariant I1: a removeLayer here would operate on a map that no
   * longer holds it). The selection itself is dropped by the reset separately,
   * through the same `active` the toolbar teardown reads.
   */
  dropRefs(): void {
    this.halo.dropRef();
  }
}
